import { Box, Button, Heading, Input, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react"
import { UploadUserExcel } from "../UploadUserExcel/UploadUserExcel"
import {
    ChakraProvider,
    FormControl,
    FormLabel,
    Checkbox,
    CheckboxGroup,
    extendTheme,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { searchQueryFun } from "./ManageUserFun";
import { UserDataTable } from "./userDataTable";
import { UpdateUser } from "../UploadUserExcel/UpdateUser";
import DeleteUsersInBulk from "../DeleteUsersInBulk/DeleteUsersInBulk";
import IdentifyUsersToDelete from "../IdentifyUsersToDelete/IdentifyUsersToDelete";
import IdentifyUsersToAdd from "../IdentifyUsersToAdd/IdentifyUsersToAdd";
import ManageVip from "./ManageVip";
import BulkJobTracker from "../../GlobalComponent/BulkJobTracker/BulkJobTracker";
import { getCompanyList } from "../UploadUserExcel/Action/uploadUserExcelFun";
import ChiDataTracking from "../ChiDataTracking/ChiDataTracking";

const activeLabelStyles = {
    transform: "scale(0.85) translateY(-24px)"
};
const theme = extendTheme({
    components: {
        Form: {
            variants: {
                floating: {
                    container: {
                        _focusWithin: {
                            label: { ...activeLabelStyles }
                        },
                        "input:not(:placeholder-shown) + label, .chakra-select__wrapper + label, textarea:not(:placeholder-shown) ~ label": {
                            ...activeLabelStyles
                        },
                        label: {
                            top: 0, left: 0, zIndex: 2, position: "absolute",
                            backgroundColor: "white", pointerEvents: "none",
                            mx: 3, px: 1, my: 2, transformOrigin: "left top"
                        }
                    }
                }
            }
        }
    }
});

interface initialPayloadInterface {
    collection: string,
    ben_id: string,
    email: string,
    name: string,
    mobile: string,
}

const initialPayload: initialPayloadInterface = {
    collection: "",
    ben_id: "",
    email: "",
    name: "",
    mobile: "",
}

export const ManageUserComponent = () => {
    const [selectedTab, setSelectedTab] = useState(0);
    const [refetchTriggers, setRefetchTriggers] = useState<Record<string, number>>({});
    const [companyList, setCompanyList] = useState<any[]>([])
    const [inputPayload, setInputPayload] = useState<initialPayloadInterface>(initialPayload)
    const [isLoading, setIsLoading] = useState<Boolean>(false)
    const [userData, setUserData] = useState<Object[]>([])
    const [localUserData, setLocalUserData] = useState<Object[]>([])
    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(10)
    const [totalPages, setTotalPages] = useState<number>(0)
    const searchRef = useRef(null)

    useEffect(() => {
        getCompanyList().then(res => setCompanyList(res?.data || []))
    }, [])

    useEffect(() => {
        setTotalPages(Math.ceil(userData.length / limit))
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const filteredData = userData.slice(startIndex, endIndex);
        setLocalUserData(filteredData)
    }, [userData, page, limit])

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "mobile") {
            const digitsOnly = value.replace(/\D/g, "");
            setInputPayload({ ...inputPayload, [name]: digitsOnly });
        } else {
            setInputPayload({ ...inputPayload, [name]: value });
        }
    }

    const handleCheckbox = (e: any) => {
        setInputPayload({ ...inputPayload, collection: e.join("&") })
    }

    const submitReq = async () => {
        setPage(1)
        setLimit(10)
        setUserData([])
        setIsLoading(true)
        let response = await searchQueryFun(inputPayload)
        response?.subResponse?.forEach((item: any) => {
            setUserData((prev) => [...prev, { ...item, collection: "Subscriber" }])
        })
        response?.benResponse?.forEach((item: any) => {
            setUserData((prev) => [...prev, { ...item, collection: "Beneficiary" }])
        })
        setIsLoading(false)
    }

    const handlePage = (pageNo: number) => setPage((prev) => prev + pageNo)
    const handleLimit = (e: React.ChangeEvent<HTMLSelectElement>) => setLimit(Number(e.target.value))
    const clearFilters = () => { setInputPayload(initialPayload); setPage(1); setLimit(10) }

    const handleTabChange = (index: number) => {
        setSelectedTab(index);
    }

    const makeOnSuccess = (action: string) => () =>
        setRefetchTriggers(prev => ({ ...prev, [action]: (prev[action] || 0) + 1 }));

    return (
        <Box style={{ width: "100%" }}>
            <Tabs index={selectedTab} onChange={handleTabChange} variant="soft-rounded" colorScheme="green">
                <TabList flexWrap="wrap" gap={1}>
                    <Tab>Identify Users To Add</Tab>
                    <Tab>Add Users</Tab>
                    <Tab>Update Users</Tab>
                    <Tab>Delete Users In Bulk</Tab>
                    <Tab>Identify Subscribers To Delete</Tab>
                    <Tab>Search & Manage</Tab>
                    <Tab>CHI Data Tracking</Tab>
                </TabList>

                <TabPanels>
                    {/* Tab 1 - Identify Users To Add */}
                    <TabPanel>
                        <Heading size="md" mb={4}>Identify The Users To Add In Bulk</Heading>
                        <IdentifyUsersToAdd key={`identify-add-${selectedTab}`} companyList={companyList} onSuccess={makeOnSuccess('IDENTIFY_ADD')} />
                        <BulkJobTracker action="IDENTIFY_ADD" refetchTrigger={refetchTriggers['IDENTIFY_ADD']} />
                    </TabPanel>

                    {/* Tab 2 - Add Multiple Users */}
                    <TabPanel>
                        <Heading size="md" mb={4}>Add Multiple Users</Heading>
                        <UploadUserExcel key={`upload-${selectedTab}`} companyList={companyList} onSuccess={makeOnSuccess('CREATE')} />
                        <BulkJobTracker action="CREATE" refetchTrigger={refetchTriggers['CREATE']} />
                    </TabPanel>

                    {/* Tab 3 - Update Users */}
                    <TabPanel>
                        <Heading size="md" mb={4}>Update Users</Heading>
                        <UpdateUser key={`update-${selectedTab}`} companyList={companyList} onSuccess={makeOnSuccess('UPDATE')} />
                        <BulkJobTracker action="UPDATE" refetchTrigger={refetchTriggers['UPDATE']} />
                    </TabPanel>

                    {/* Tab 4 - Delete Users In Bulk */}
                    <TabPanel>
                        <Heading size="md" mb={4}>Delete Users In Bulk</Heading>
                        <DeleteUsersInBulk key={`delete-bulk-${selectedTab}`} companyList={companyList} onSuccess={makeOnSuccess('DELETE')} />
                        <BulkJobTracker action="DELETE" refetchTrigger={refetchTriggers['DELETE']} />
                    </TabPanel>

                    {/* Tab 5 - Identify Users To Delete */}
                    <TabPanel>
                        <Heading size="md" mb={4}>Identify The Subscribers To Delete In Bulk</Heading>
                        <IdentifyUsersToDelete key={`identify-delete-${selectedTab}`} companyList={companyList} onSuccess={makeOnSuccess('IDENTIFY_DELETE')} />
                        <BulkJobTracker action="IDENTIFY_DELETE" refetchTrigger={refetchTriggers['IDENTIFY_DELETE']} />
                    </TabPanel>

                    {/* Tab 6 - Search & Manage */}
                    <TabPanel>
                        <Box>
                            <Heading size="md" mb={4}>Delete Users</Heading>
                            <Box mt={"10px"} display={"flex"} gap={"50px"} alignItems={"center"} flexWrap="wrap">
                                <Box>
                                    <CheckboxGroup onChange={handleCheckbox}>
                                        <Checkbox display={"block"} value={"ben"}>Search By Beneficiary</Checkbox>
                                        <Checkbox display={"block"} value={"sub"}>Search By Subscriber</Checkbox>
                                    </CheckboxGroup>
                                </Box>
                                <Box>
                                    <ChakraProvider theme={theme}>
                                        <FormControl variant="floating">
                                            <Input placeholder=" " name="ben_id" onChange={handleInput} value={inputPayload.ben_id} />
                                            <FormLabel>Beneficiary Id</FormLabel>
                                        </FormControl>
                                    </ChakraProvider>
                                </Box>
                                <Box>
                                    <ChakraProvider theme={theme}>
                                        <FormControl variant="floating">
                                            <Input placeholder=" " name="email" onChange={handleInput} value={inputPayload.email} />
                                            <FormLabel>Email</FormLabel>
                                        </FormControl>
                                    </ChakraProvider>
                                </Box>
                                <Box>
                                    <ChakraProvider theme={theme}>
                                        <FormControl variant="floating">
                                            <Input placeholder=" " name="name" onChange={handleInput} value={inputPayload.name} />
                                            <FormLabel>Name</FormLabel>
                                        </FormControl>
                                    </ChakraProvider>
                                </Box>
                                <Box>
                                    <ChakraProvider theme={theme}>
                                        <FormControl variant="floating">
                                            <Input placeholder=" " name="mobile" onChange={handleInput} value={inputPayload.mobile} />
                                            <FormLabel>Phone Number</FormLabel>
                                        </FormControl>
                                    </ChakraProvider>
                                </Box>
                                <Box>
                                    <Button onClick={clearFilters} colorScheme="green">Clear Filters</Button>
                                </Box>
                            </Box>
                            <Box mt={"20px"} display={"flex"} justifyContent={"center"}>
                                <Button padding={"20px"} width={"200px"} onClick={submitReq} ref={searchRef} colorScheme="green">Search</Button>
                            </Box>
                        </Box>

                        {isLoading === false && <Box mt={"30px"}>
                            {localUserData.length !== 0 && <UserDataTable data={localUserData} searchRef={searchRef} totalPages={totalPages} page={page} limit={limit} handlePage={handlePage} handleLimit={handleLimit} />}
                        </Box>}
                        {isLoading && <Spinner ml={"48%"} mt={"150px"} thickness='4px' speed='0.65s' emptyColor='gray.200' color='green.500' size='xl' />}

                        <Box mt={6}>
                            <ManageVip setUserData={setUserData} />
                        </Box>
                    </TabPanel>

                    {/* Tab 7 - CHI Data Tracking */}
                    <TabPanel>
                        <ChiDataTracking key={`chi-tracking-${selectedTab}`} />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    )
}
